import {
  DRAWER_ID_PARAM,
  markShouldRestore,
  readListSnapshot,
} from '@/features/approving_invoice_reports/utils/listSnapshotStorage';
import { useKeepApprovalSearchConditionsFlag } from '@/hooks/feature_flag/useKeepApprovalSearchConditionsFlag';
import { emitDisabledBeforeUnload } from '@/hooks/useReloadWindow';
import { useCallback, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

export const useGetInvoiceReportPath = () => {
  const location = useLocation();
  const { pathname } = location;
  const adminOrIndexParams = useParams<{
    report_id: string;
    report_type_id: string;
  }>();

  const approvalParams = useParams<{ report_id: string }>();

  // NA-5815 / NA-5831: with the flag on, "show" on the approving route is the
  // Approval list with this request's drawer open, not the Rails detail page.
  const keepSearchConditionsEnabled = useKeepApprovalSearchConditionsFlag();

  return useMemo(() => {
    const adminPattern = /admin_invoice_reports/;
    const approvalPattern = /approving_invoice_reports/;

    if (adminPattern.test(pathname)) {
      return {
        currentPathRole: 'admin',
        index: '/admin_invoice_reports',
        show: `/report_types/${adminOrIndexParams.report_type_id}/admin_invoice_reports/${adminOrIndexParams.report_id}`,
        selectApprover: `/reports/${adminOrIndexParams.report_id}/route/edit`,
      };
    }

    if (approvalPattern.test(pathname)) {
      return {
        currentPathRole: 'approval',
        index: '/approving_invoice_reports',
        show: keepSearchConditionsEnabled
          ? `/approving_invoice_reports?${DRAWER_ID_PARAM}=${approvalParams.report_id}`
          : `/approving_invoice_reports/${approvalParams.report_id}`,
        selectApprover: `/reports/${approvalParams.report_id}/route/edit`,
      };
    }

    return {
      currentPathRole: 'application',
      index: '/invoice_reports',
      show: `/report_types/${adminOrIndexParams.report_type_id}/invoice_reports/${adminOrIndexParams.report_id}`,
      selectApprover: `/reports/${adminOrIndexParams.report_id}/route/edit`,
    };
  }, [
    pathname,
    adminOrIndexParams,
    approvalParams,
    keepSearchConditionsEnabled,
  ]);
};

export const useInvoiceReportNavigation = () => {
  const paths = useGetInvoiceReportPath();
  const { report_id: reportId } = useParams<{ report_id: string }>();

  // NA-5815: keep the Approval-list search conditions (approving route + flag on)
  const keepSearchConditionsEnabled = useKeepApprovalSearchConditionsFlag();
  const keepsApprovalSearchConditions =
    paths.currentPathRole === 'approval' && keepSearchConditionsEnabled;

  const goToInvoiceReportIndex = useCallback(() => {
    emitDisabledBeforeUnload();
    // back to the Approval list → let it restore its saved search
    if (keepsApprovalSearchConditions) {
      markShouldRestore({ shouldOpenNextTarget: false });
    }
    window.location.href = paths.index;
  }, [paths.index, keepsApprovalSearchConditions]);

  const goToInvoiceReportShow = useCallback(() => {
    emitDisabledBeforeUnload();
    // came here from the Approval list for THIS request (a snapshot was saved
    // with its id) → bare list + intent: the list restores its saved search and
    // reopens the drawer. A snapshot left for another request is ignored.
    // Otherwise paths.show already points at the list with this request's
    // drawer open (flag on) or at the detail page (flag off / other roles).
    const cameFromListForThisReport =
      keepsApprovalSearchConditions &&
      reportId !== undefined &&
      readListSnapshot()?.reportId === reportId;
    if (cameFromListForThisReport) {
      markShouldRestore({ shouldOpenNextTarget: false });
      window.location.href = paths.index;
      return;
    }
    window.location.href = paths.show;
  }, [paths.show, paths.index, keepsApprovalSearchConditions, reportId]);

  const goToInvoiceReportSelectApprover = useCallback(() => {
    emitDisabledBeforeUnload();
    window.location.href = paths.selectApprover;
  }, [paths.selectApprover]);

  const isApproverRoleFromPath = useMemo(() => {
    return (
      paths.currentPathRole === 'admin' || paths.currentPathRole === 'approval'
    );
  }, [paths.currentPathRole]);

  return {
    goToInvoiceReportIndex,
    goToInvoiceReportShow,
    goToInvoiceReportSelectApprover,
    isApproverRoleFromPath,
  };
};
